// ArrangementHistory.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../services/firebase';
import { ref, onValue } from 'firebase/database';

const ArrangementHistory = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    withSubstitute: 0,
    noSubstitute: 0,
    byReason: {}
  });

  const today = selectedDate.toISOString().split('T')[0];

  // Load master data
  useEffect(() => {
    const teachersRef = ref(db, 'teachers');
    onValue(teachersRef, (snapshot) => {
      const data = snapshot.val() || {};
      const teacherList = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      setTeachers(teacherList);
    });

    const classesRef = ref(db, 'classes');
    onValue(classesRef, (snapshot) => {
      const data = snapshot.val() || {};
      const classList = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      setClasses(classList);
    });

    const subjectsRef = ref(db, 'subjects');
    onValue(subjectsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const subjectList = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      setSubjects(subjectList);
    });
  }, []);

  // Load history for selected date
  useEffect(() => {
    setLoading(true);
    const historyRef = ref(db, `arrangementHistory/${today}`);
    onValue(historyRef, (snapshot) => {
      const data = snapshot.val() || {};
      const historyArray = Object.keys(data).map((key) => ({
        id: key,
        ...data[key]
      }));
      setHistoryData(historyArray);
      calculateStats(historyArray);
      setLoading(false);
    });
  }, [today]);

  // Calculate statistics
  const calculateStats = (data) => {
    const total = data.length;
    const withSubstitute = data.filter(d => d.substituteTeacher && d.substituteTeacher !== 'No Teacher Available').length;
    const noSubstitute = data.filter(d => d.substituteTeacher === 'No Teacher Available').length;
    
    const byReason = {};
    data.forEach(d => {
      if (d.substituteReason) {
        byReason[d.substituteReason] = (byReason[d.substituteReason] || 0) + 1;
      }
    });

    setStats({ total, withSubstitute, noSubstitute, byReason });
  };

  // Filter history data
  const getFilteredData = () => {
    return historyData.filter(item => {
      if (filterTeacher && item.absentTeacher !== filterTeacher) return false;
      if (filterClass && item.class !== filterClass) return false;
      if (filterSubject && item.subject !== filterSubject) return false;
      if (filterPeriod && item.period !== filterPeriod) return false;
      return true;
    });
  };

  const filteredData = getFilteredData();

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Export as CSV
  const exportCSV = () => {
    if (filteredData.length === 0) return;
    
    const headers = ['Date', 'Day', 'Period', 'Class', 'Subject', 'Absent Teacher', 'Substitute', 'Reason'];
    const rows = filteredData.map(item => [
      formatDate(selectedDate),
      item.day,
      item.period,
      item.class,
      item.subject,
      item.absentTeacher,
      item.substituteTeacher,
      item.substituteReason
    ]);

    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arrangement_history_${today}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print report
  const printReport = () => {
    window.print();
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📊 Arrangement History</h1>

      {/* Date Selector */}
      <div style={styles.dateSection}>
        <div style={styles.datePickerWrapper}>
          <label style={styles.label}>Select Date</label>
          <input
            type="date"
            value={today}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            style={styles.dateInput}
          />
          <span style={styles.dateDisplay}>{formatDate(selectedDate)}</span>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={{...styles.statCard, borderLeft: '6px solid #2563eb'}}>
          <h4 style={styles.statLabel}>Total Arrangements</h4>
          <h2 style={{...styles.statValue, color: '#2563eb'}}>{stats.total}</h2>
        </div>
        <div style={{...styles.statCard, borderLeft: '6px solid #16a34a'}}>
          <h4 style={styles.statLabel}>With Substitute</h4>
          <h2 style={{...styles.statValue, color: '#16a34a'}}>{stats.withSubstitute}</h2>
        </div>
        <div style={{...styles.statCard, borderLeft: '6px solid #dc2626'}}>
          <h4 style={styles.statLabel}>No Substitute</h4>
          <h2 style={{...styles.statValue, color: '#dc2626'}}>{stats.noSubstitute}</h2>
        </div>
        <div style={{...styles.statCard, borderLeft: '6px solid #f59e0b'}}>
          <h4 style={styles.statLabel}>Reasons</h4>
          <div style={styles.reasonTags}>
            {Object.entries(stats.byReason).map(([reason, count]) => (
              <span key={reason} style={styles.reasonTag}>
                {reason}: {count}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterSection}>
        <h3 style={styles.filterTitle}>Filters</h3>
        <div style={styles.filterGrid}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Teacher</label>
            <select
              value={filterTeacher}
              onChange={(e) => setFilterTeacher(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Teachers</option>
              {teachers.map(t => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Class</label>
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Classes</option>
              {classes.map(c => {
                const className = `${c.className}_${c.section}`.replace(/_/g, ' ');
                return (
                  <option key={c.id} value={className}>{className}</option>
                );
              })}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Subject</label>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Subjects</option>
              {subjects.map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Period</label>
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Periods</option>
              {['1','2','3','4','5','6','7'].map(p => (
                <option key={p} value={p}>Period {p}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.filterActions}>
          <button onClick={() => {
            setFilterTeacher('');
            setFilterClass('');
            setFilterSubject('');
            setFilterPeriod('');
          }} style={styles.clearButton}>
            Clear Filters
          </button>
          <button onClick={exportCSV} style={styles.exportButton}>
            📥 Export CSV
          </button>
          <button onClick={printReport} style={styles.printButton}>
            🖨️ Print Report
          </button>
        </div>
      </div>

      {/* History Table */}
      {loading ? (
        <div style={styles.loading}>Loading history...</div>
      ) : filteredData.length > 0 ? (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Day</th>
                <th style={styles.th}>Period</th>
                <th style={styles.th}>Class</th>
                <th style={styles.th}>Subject</th>
                <th style={styles.th}>Absent Teacher</th>
                <th style={styles.th}>Substitute</th>
                <th style={styles.th}>Reason</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={index} style={styles.tr}>
                  <td style={styles.td}>{formatDate(selectedDate)}</td>
                  <td style={styles.td}>{item.day}</td>
                  <td style={styles.td}>Period {item.period}</td>
                  <td style={styles.td}>{item.class}</td>
                  <td style={styles.td}>{item.subject}</td>
                  <td style={styles.td}>
                    <span style={styles.absentBadge}>{item.absentTeacher}</span>
                  </td>
                  <td style={styles.td}>
                    {item.substituteTeacher && item.substituteTeacher !== 'No Teacher Available' ? (
                      <span style={styles.substituteBadge}>{item.substituteTeacher}</span>
                    ) : (
                      <span style={styles.noSubstituteBadge}>No Substitute</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.reasonBadge}>{item.substituteReason}</span>
                  </td>
                  <td style={styles.td}>
                    {item.substituteTeacher && item.substituteTeacher !== 'No Teacher Available' ? (
                      <span style={styles.completedBadge}>✅ Completed</span>
                    ) : (
                      <span style={styles.pendingBadge}>⚠️ Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={styles.noData}>
          <p>No arrangements found for this date.</p>
          <p style={styles.noDataSub}>Try selecting a different date.</p>
        </div>
      )}

      {/* Summary */}
      {filteredData.length > 0 && (
        <div style={styles.summary}>
          <p style={styles.summaryText}>
            Showing <strong>{filteredData.length}</strong> of <strong>{historyData.length}</strong> arrangements
          </p>
          <p style={styles.summaryText}>
            <span style={{color: '#16a34a'}}>✅ {stats.withSubstitute} with substitute</span>
            {' '}·{' '}
            <span style={{color: '#dc2626'}}>❌ {stats.noSubstitute} without substitute</span>
          </p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px 20px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '600',
    color: '#0a1e3c',
    marginBottom: '24px',
  },
  dateSection: {
    marginBottom: '24px',
  },
  datePickerWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    backgroundColor: '#f8faff',
    padding: '16px 20px',
    borderRadius: '12px',
    border: '1px solid #e2e9f2',
  },
  label: {
    fontWeight: '500',
    color: '#1f3a6b',
  },
  dateInput: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #d3deed',
    fontSize: '1rem',
    outline: 'none',
  },
  dateDisplay: {
    fontWeight: '500',
    color: '#0a1e3c',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    backgroundColor: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid #e2e9f2',
  },
  statLabel: {
    margin: 0,
    color: '#666',
    fontSize: '0.9rem',
    fontWeight: '500',
  },
  statValue: {
    margin: '8px 0 0',
    fontSize: '1.8rem',
    fontWeight: '600',
  },
  reasonTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '8px',
  },
  reasonTag: {
    backgroundColor: '#f0f4fe',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    color: '#1f3a6b',
  },
  filterSection: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid #e2e9f2',
    marginBottom: '24px',
  },
  filterTitle: {
    margin: '0 0 16px 0',
    fontSize: '1.1rem',
    fontWeight: '500',
    color: '#0a1e3c',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: '#1f3a6b',
  },
  filterSelect: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #d3deed',
    fontSize: '0.95rem',
    outline: 'none',
    backgroundColor: 'white',
  },
  filterActions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  clearButton: {
    padding: '8px 20px',
    backgroundColor: '#f0f4fe',
    color: '#0a1e3c',
    border: '1px solid #d3deed',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
  },
  exportButton: {
    padding: '8px 20px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
  },
  printButton: {
    padding: '8px 20px',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
  },
  tableWrapper: {
    overflowX: 'auto',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid #e2e9f2',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '12px 16px',
    backgroundColor: '#f8faff',
    textAlign: 'left',
    fontWeight: '600',
    color: '#0a1e3c',
    borderBottom: '2px solid #e2e9f2',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #e9eef5',
    fontSize: '0.9rem',
  },
  tr: {
    ':hover': {
      backgroundColor: '#f8faff',
    },
  },
  absentBadge: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  substituteBadge: {
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  noSubstituteBadge: {
    backgroundColor: '#fef3c7',
    color: '#f59e0b',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  reasonBadge: {
    backgroundColor: '#e0e7ff',
    color: '#4f46e5',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  completedBadge: {
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  pendingBadge: {
    backgroundColor: '#fef3c7',
    color: '#f59e0b',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  noData: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #e2e9f2',
  },
  noDataSub: {
    color: '#999',
    fontSize: '0.9rem',
  },
  summary: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '8px',
  },
  summaryText: {
    fontSize: '0.9rem',
    color: '#666',
    margin: 0,
  },
};

export default ArrangementHistory;